import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
    } catch {
      // intentionally swallow — always show same confirmation to avoid email enumeration
    }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
            alt="Whiplash Film & Creative"
            className="h-8"
          />
        </div>
        <div className="border border-zinc-800 bg-zinc-900/60 rounded-md p-7">
          {!sent ? (
            <>
              <div className="text-overline text-zinc-500 mb-1">Password Reset</div>
              <h1 className="font-heading text-2xl text-zinc-100 mb-1 leading-tight">
                Forgot password?
              </h1>
              <p className="text-sm text-zinc-500 mb-6">
                Enter your email and we&apos;ll send you a link to reset it.
              </p>
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="forgot-email-input"
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                  />
                </div>
                <Button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</>
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h1 className="font-heading text-xl text-zinc-100 mb-2">Check your inbox</h1>
              <p className="text-sm text-zinc-500">
                If an account exists for <strong>{email}</strong>, we sent a password reset link.
                It expires in 1 hour.
              </p>
            </div>
          )}
          <div className="text-center mt-5">
            <Link
              to="/login"
              className="text-xs text-zinc-500 hover:text-amber-500 transition-colors"
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
